import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import { useNotification } from '../../contexts/NotificationContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';

const P = {
  blue: '#0A0A0A',
  green: '#16A34A',
  purple: '#525252',
  orange: '#D97706',
  red: '#DC2626',
  gray: '#6B6B6B',
  dark: '#0A0A0A',
  border: '#E5E5E5',
  bg: '#f5f5f7',
};

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

const TAG_PALETTE = [
  { bg: 'rgba(10,10,10,0.06)', color: '#0A0A0A' },
  { bg: 'rgba(10,10,10,0.10)', color: '#0A0A0A' },
  { bg: '#F0F0F0', color: '#404040' },
  { bg: '#EDEDED', color: '#262626' },
  { bg: 'rgba(10,10,10,0.05)', color: '#525252' },
  { bg: '#F5F5F5', color: '#171717' },
  { bg: 'rgba(10,10,10,0.08)', color: '#333333' },
  { bg: '#EAEAEA', color: '#0A0A0A' },
];

const tagColor = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
};

const getContactHref = (lead) => {
  if (lead.email) return `mailto:${lead.email}`;
  if (lead.telefono) return `tel:${lead.telefono}`;
  return null;
};

// estado is a Spanish enum value persisted in the DB ('Nuevo' | 'Contactado' |
// 'No contactado') — never translate the value itself, only how it displays.
const estadoLabel = (estado, t) => {
  if (estado === 'Nuevo') return t('leads.estado.nuevo', 'Nuevo');
  if (estado === 'Contactado') return t('leads.estado.contactado', 'Contactado');
  if (estado === 'No contactado') return t('leads.estado.noContactado', 'No contactado');
  return estado;
};

const timeAgo = (dateStr, t) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return t('leads.timeAgo.minutes', `Hace ${Math.max(1, mins)}m`).replace('{n}', Math.max(1, mins));
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('leads.timeAgo.hours', `Hace ${hours}h`).replace('{n}', hours);
  const days = Math.floor(hours / 24);
  return t('leads.timeAgo.days', `Hace ${days}d`).replace('{n}', days);
};

const urgencyBadge = (lead, t) => {
  const hours = (Date.now() - new Date(lead.fecha_identificado).getTime()) / 3600000;
  if (lead.estado !== 'Nuevo') {
    return { label: timeAgo(lead.fecha_identificado, t), bg: 'rgba(0,0,0,0.05)', color: P.gray };
  }
  if (hours > 24) return { label: `${t('leads.urgent', 'Urgente')} · ${timeAgo(lead.fecha_identificado, t)}`, bg: P.red, color: '#fff' };
  if (hours > 6) return { label: timeAgo(lead.fecha_identificado, t), bg: 'rgba(217,119,6,0.16)', color: '#D97706' };
  return { label: timeAgo(lead.fecha_identificado, t), bg: 'rgba(22,163,74,0.14)', color: '#16A34A' };
};

const estadoBadgeClass = (estado) => (
  estado === 'Nuevo' ? 'lp-badge lp-badge-nuevo' :
  estado === 'Contactado' ? 'lp-badge lp-badge-contactado' :
  estado === 'No contactado' ? 'lp-badge lp-badge-no-contactado' :
  'lp-badge lp-badge-default'
);

const CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
.lp-wrap { font-family: ${FONT_UI}; color: ${P.dark}; }
.lp-header-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; padding-bottom: 20px; margin-bottom: 4px; border-bottom: 1px solid rgba(0,0,0,0.06); }
.lp-header-title { font-family: ${FONT}; font-size: 30px; font-weight: 300; letter-spacing: -0.01em; color: ${P.dark}; margin: 0; }
.lp-header-sub { font-size: 14px; color: ${P.gray}; margin: 4px 0 0; font-weight: 400; }
.lp-metrics { display: flex; background: #fff; border-radius: 14px; border: 1px solid ${P.border}; overflow: hidden; }
.lp-metric-card { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 10px; border-right: 1px solid ${P.border}; }
.lp-metric-card:last-child { border-right: none; }
.lp-metric-value { font-size: 17px; font-weight: 700; letter-spacing: -0.3px; color: ${P.dark}; line-height: 1; }
.lp-metric-label { font-size: 11.5px; color: ${P.gray}; font-weight: 500; margin-top: 0; }
.lp-filters { background: #fff; border-radius: 16px; border: 1px solid ${P.border}; padding: 14px 18px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.lp-input { height: 36px; padding: 0 12px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 13px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; transition: border-color .15s, box-shadow .15s, background .15s; }
.lp-input:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); background: #fff; }
.lp-input-search { flex: 1; min-width: 200px; }
.lp-select { height: 36px; padding: 0 10px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 13px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.lp-select:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); }
.lp-card-name-group { display: inline-flex; align-items: center; gap: 5px; }
.lp-edit-icon-btn { background: none; border: none; padding: 3px; margin: 0; cursor: pointer; color: ${P.gray}; display: flex; align-items: center; justify-content: center; border-radius: 6px; transition: background .15s, color .15s; flex-shrink: 0; }
.lp-edit-icon-btn:hover { background: rgba(10,10,10,0.06); color: ${P.blue}; }
.lp-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 0.2px; white-space: nowrap; }
.lp-badge-nuevo { background: rgba(22,163,74,0.12); color: #16A34A; }
.lp-badge-contactado { background: rgba(37,99,235,0.12); color: #2563EB; }
.lp-badge-no-contactado { background: rgba(217,119,6,0.12); color: ${P.orange}; }
.lp-badge-default { background: rgba(0,0,0,0.06); color: ${P.gray}; }
.lp-urgency-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.1px; white-space: nowrap; }
.lp-tags { display: flex; flex-wrap: wrap; gap: 5px; }
.lp-tag { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; }
.lp-empty { background: #fff; border-radius: 18px; border: 1px solid ${P.border}; padding: 64px 24px; text-align: center; }
.lp-empty-text { font-size: 15px; color: ${P.gray}; }
.lp-loading { text-align: center; padding: 64px 24px; }
.lp-loading-text { font-size: 15px; color: ${P.gray}; }
.lp-btn-primary { flex: 1; height: 34px; background: ${P.blue}; color: #fff; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: inherit; transition: opacity .15s; }
.lp-btn-primary:hover { opacity: 0.86; }

/* List (table + mobile stacked cards) — mirrors HubPanel's .hb-table / .hb-mobile-card */
.lp-table-wrap { background: #fff; border-radius: 14px; border: 1px solid ${P.border}; overflow: hidden; }
.lp-table { width: 100%; border-collapse: collapse; }
.lp-table th { text-align: left; font-size: 11.5px; font-weight: 600; color: ${P.gray}; text-transform: uppercase; letter-spacing: .4px; padding: 12px 16px; border-bottom: 1px solid ${P.border}; background: ${P.bg}; }
.lp-table td { padding: 14px 16px; border-bottom: 1px solid ${P.border}; font-size: 13.5px; vertical-align: top; }
.lp-table tr:last-child td { border-bottom: none; }
.lp-table-row { cursor: pointer; transition: background .12s; }
.lp-table-row:hover { background: #FAFAFA; }
.lp-table-name { font-size: 14px; font-weight: 600; color: ${P.dark}; }
.lp-table-info-row { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: ${P.gray}; margin-bottom: 3px; }
.lp-table-info-row:last-child { margin-bottom: 0; }
.lp-table-date { color: ${P.gray}; font-size: 12.5px; white-space: nowrap; }
.lp-table-actions { display: flex; gap: 6px; justify-content: flex-end; }
.lp-table-btn { height: 30px; padding: 0 10px; border-radius: 8px; font-size: 12px; font-weight: 500; display: inline-flex; align-items: center; gap: 4px; border: 1px solid ${P.border}; background: #fff; color: ${P.dark}; text-decoration: none; cursor: pointer; font-family: inherit; transition: background .15s, border-color .15s; white-space: nowrap; }
.lp-table-btn:hover { background: #FAFAFA; border-color: #D4D4D4; }
.lp-table-btn-disabled { opacity: 0.4; cursor: not-allowed; }
.lp-table-btn-disabled:hover { background: #fff; border-color: ${P.border}; }
.lp-table-quick { display: flex; gap: 5px; margin-top: 6px; }
.lp-quick-btn { height: 24px; padding: 0 9px; border: none; border-radius: 7px; font-size: 10.5px; font-weight: 600; cursor: pointer; font-family: inherit; transition: opacity .15s; white-space: nowrap; }
.lp-quick-btn:hover { opacity: 0.82; }
.lp-quick-btn-contact { background: #0A0A0A; color: #fff; }
.lp-quick-btn-skip { background: rgba(0,0,0,0.06); color: ${P.gray}; }
@media(max-width:900px){ .lp-table-wrap { display: none; } }

/* Locked row (free plan, over the monthly quota) */
.lp-locked-row { cursor: pointer; background: #FAFAFA; }
.lp-locked-row:hover { background: #F5F5F5; }
.lp-locked-bar { display: inline-block; height: 10px; border-radius: 4px; background: rgba(0,0,0,0.09); }
.lp-locked-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.2px; background: rgba(0,113,227,0.1); color: #0071e3; white-space: nowrap; }
.lp-locked-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; background: rgba(0,113,227,0.06); border: 1px solid rgba(0,113,227,0.16); border-radius: 14px; padding: 12px 16px; }
.lp-locked-banner-text { font-size: 13px; color: #0A0A0A; }
.lp-locked-banner-btn { height: 32px; padding: 0 14px; border: none; border-radius: 980px; background: #0071e3; color: #fff; font-size: 12.5px; font-weight: 500; cursor: pointer; font-family: inherit; white-space: nowrap; transition: opacity .15s; }
.lp-locked-banner-btn:hover { opacity: 0.88; }

/* First-visit onboarding (popup) */
.lp-onboarding-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 150; padding: 16px; }
.lp-onboarding-modal { background: #fff; border-radius: 20px; box-shadow: 0 24px 60px rgba(0,0,0,0.18); max-width: 380px; width: 100%; padding: 30px 26px; text-align: center; }
.lp-onboarding-icon { width: 46px; height: 46px; border-radius: 13px; background: rgba(0,0,0,0.05); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; color: ${P.dark}; }
.lp-onboarding-title { font-size: 17px; font-weight: 600; color: ${P.dark}; margin: 0 0 8px; letter-spacing: -0.01em; }
.lp-onboarding-desc { font-size: 13.5px; color: ${P.gray}; line-height: 1.55; margin: 0 0 22px; }
.lp-onboarding-close { width: 100%; height: 40px; border: none; border-radius: 980px; background: #0A0A0A; color: #fff; font-size: 13.5px; font-weight: 500; cursor: pointer; font-family: inherit; transition: opacity .15s; }
.lp-onboarding-close:hover { opacity: 0.86; }

.lp-mobile-list { display: none; flex-direction: column; gap: 10px; }
@media(max-width:900px){ .lp-mobile-list { display: flex; } }
.lp-mobile-card { background: #fff; border: 1px solid ${P.border}; border-radius: 14px; padding: 14px; cursor: pointer; transition: box-shadow .15s; }
.lp-mobile-card:active { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
.lp-mobile-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.lp-mobile-name { font-size: 15px; font-weight: 600; color: ${P.dark}; }
.lp-mobile-badges { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; align-items: center; }
.lp-mobile-info { margin-top: 8px; display: flex; flex-direction: column; gap: 4px; }
.lp-mobile-actions { display: flex; gap: 8px; margin-top: 10px; }
.lp-mobile-action-btn { flex: 1; height: 34px; border-radius: 10px; font-size: 12.5px; font-weight: 500; display: inline-flex; align-items: center; justify-content: center; gap: 5px; border: 1px solid ${P.border}; background: #fff; color: ${P.dark}; text-decoration: none; cursor: pointer; font-family: inherit; }
.lp-mobile-action-btn:hover { background: #FAFAFA; }
.lp-mobile-action-btn-disabled { opacity: 0.4; cursor: not-allowed; }
.lp-mobile-action-btn-disabled:hover { background: #fff; }

.lp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 16px; }
.lp-modal { background: #fff; border-radius: 20px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 72px rgba(0,0,0,0.22); }
.lp-modal-inner { padding: 28px; }
.lp-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; }
.lp-modal-title { font-size: 20px; font-weight: 700; color: ${P.dark}; margin: 0; letter-spacing: -0.3px; }
.lp-modal-close { width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.06); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: ${P.gray}; transition: background .15s; }
.lp-modal-close:hover { background: rgba(0,0,0,0.10); }
.lp-avatar { width: 72px; height: 72px; background: #F0F0F0; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; overflow: hidden; }
.lp-avatar-letter { font-size: 30px; font-weight: 700; color: ${P.blue}; }
.lp-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.lp-row-avatar { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.lp-field { margin-bottom: 14px; }
.lp-label { display: block; font-size: 11px; font-weight: 600; color: ${P.gray}; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.6px; }
.lp-field-input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 14px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color .15s, box-shadow .15s, background .15s; }
.lp-field-input:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); background: #fff; }
.lp-field-textarea { width: 100%; padding: 10px 12px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 14px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; box-sizing: border-box; resize: vertical; transition: border-color .15s, box-shadow .15s; }
.lp-field-textarea:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); background: #fff; }
.lp-field-select { width: 100%; height: 40px; padding: 0 12px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 14px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; box-sizing: border-box; cursor: pointer; }
.lp-field-select:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); }
.lp-tag-row { display: flex; gap: 8px; margin-bottom: 8px; }
.lp-tag-input { flex: 1; height: 36px; padding: 0 12px; border: 1px solid ${P.border}; border-radius: 10px; font-size: 13px; color: ${P.dark}; background: ${P.bg}; font-family: inherit; outline: none; transition: border-color .15s; }
.lp-tag-input:focus { border-color: ${P.blue}; box-shadow: 0 0 0 3px rgba(10,10,10,0.08); }
.lp-tag-add { height: 36px; padding: 0 16px; background: ${P.blue}; color: #fff; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: inherit; transition: opacity .15s; }
.lp-tag-add:hover { opacity: 0.86; }
.lp-modal-tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.lp-modal-tag-remove { background: none; border: none; cursor: pointer; font-size: 15px; line-height: 1; padding: 0; opacity: 0.55; transition: opacity .12s, color .12s; }
.lp-modal-tag-remove:hover { opacity: 1; color: ${P.red}; }
.lp-modal-footer { display: flex; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid ${P.border}; }
.lp-btn-cancel { flex: 1; height: 40px; background: transparent; border: 1px solid ${P.border}; border-radius: 10px; font-size: 14px; font-weight: 500; color: ${P.dark}; cursor: pointer; font-family: inherit; transition: background .15s; }
.lp-btn-cancel:hover { background: rgba(0,0,0,0.04); }
.lp-btn-save { flex: 1; height: 40px; background: ${P.blue}; border: none; border-radius: 10px; font-size: 14px; font-weight: 500; color: #fff; cursor: pointer; font-family: inherit; transition: opacity .15s; }
.lp-btn-save:hover { opacity: 0.86; }
@media (max-width: 768px) {
  .lp-header-title { font-size: 21px; }
  .lp-header-sub { font-size: 11px; }
  .lp-metric-card { padding: 6px 6px; gap: 4px; }
  .lp-metric-value { font-size: 13px; }
  .lp-metric-label { font-size: 10.5px; }
  .lp-filters { padding: 9px 11px; gap: 6px; }
  .lp-input, .lp-select { height: 32px; font-size: 12px; }
  .lp-badge { padding: 2px 7px; font-size: 10.5px; }
  .lp-urgency-badge { padding: 2px 6px; font-size: 10.5px; }
  .lp-tag { padding: 2px 6px; font-size: 10.5px; }
  .lp-mobile-card { padding: 10px; border-radius: 10px; }
  .lp-mobile-name { font-size: 12.5px; }
  .lp-mobile-badges { gap: 4px; margin-top: 6px; }
  .lp-mobile-info { margin-top: 6px; gap: 3px; }
  .lp-mobile-actions { gap: 5px; margin-top: 7px; }
  .lp-mobile-action-btn { min-width: 70px; height: 34px; font-size: 10.5px; gap: 3px; border-radius: 8px; }
  .lp-avatar { width: 46px; height: 46px; margin: 0 auto 14px; }
  .lp-avatar-letter { font-size: 20px; }
  .lp-modal-inner { padding: 15px; }
  .lp-modal-title { font-size: 15px; }
  .lp-modal-close { width: 26px; height: 26px; }
  .lp-field-input, .lp-field-select { height: 36px; font-size: 12.5px; }
  .lp-field-textarea { font-size: 12.5px; }
  .lp-label { font-size: 10.5px; }
  .lp-tag-input, .lp-tag-add { height: 32px; font-size: 12px; }
  .lp-modal-tag { font-size: 10.5px; padding: 3px 8px; }
  .lp-btn-cancel, .lp-btn-save { height: 36px; font-size: 12.5px; }
  .lp-empty { padding: 40px 16px; }
  .lp-empty-icon-mobile { width: 38px !important; height: 38px !important; }
  .lp-empty-text { font-size: 12.5px; }
  .lp-empty-text-sub { font-size: 11px !important; }
}
`;

const LeadsPanel = ({ profile, user }) => {
  const { toast } = useNotification();
  const { hasFeature, openUpgrade } = usePlan();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-ES';
  const onboardingKey = user?.id ? `leads_onboarding_seen_${user.id}` : null;
  const [showOnboarding, setShowOnboarding] = useState(
    () => !!onboardingKey && !localStorage.getItem(onboardingKey)
  );
  const dismissOnboarding = () => {
    if (onboardingKey) localStorage.setItem(onboardingKey, '1');
    setShowOnboarding(false);
  };
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const [estadoFilter, setEstadoFilter] = useState('Todos');
  const [etiquetaFilter, setEtiquetaFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const [editedLead, setEditedLead] = useState(null);
  const [newTag, setNewTag] = useState('');

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    if (profile?.id) {
      loadLeads();
    }
  }, [profile, estadoFilter, etiquetaFilter, campaignFilter, fechaDesde, fechaHasta, busqueda]);

  useEffect(() => {
    if (!hasFeature('campaigns')) return;
    axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => setCampaigns(res.data?.campaigns || []))
      .catch(() => {});
  }, [hasFeature]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (estadoFilter && estadoFilter !== 'Todos') params.append('estado', estadoFilter);
      if (etiquetaFilter) params.append('etiqueta', etiquetaFilter);
      if (campaignFilter) params.append('campaign_id', campaignFilter);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (busqueda) params.append('busqueda', busqueda);

      const response = await axios.get(
        `${API}/profiles/${profile.id}/leads?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setLeads(response.data);
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLead = async (lead) => {
    if (lead.locked) {
      openUpgrade('leads');
      return;
    }
    setSelectedLead(lead);
    setEditedLead({ ...lead });
    setShowLeadModal(true);
  };

  const handleSaveLead = async () => {
    try {
      await axios.put(
        `${API}/leads/${editedLead.id}`,
        editedLead,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setShowLeadModal(false);
      loadLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(t('leads.toast.updateError', 'Error al actualizar el lead'));
    }
  };

  const handleQuickStatusChange = async (lead, estado) => {
    if (lead.estado === estado) return;
    try {
      await axios.put(
        `${API}/leads/${lead.id}`,
        { estado },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      loadLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast.error(t('leads.toast.moveError', 'No se pudo mover el lead. Intenta de nuevo.'));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editedLead.etiquetas.includes(newTag.trim())) {
      setEditedLead({
        ...editedLead,
        etiquetas: [...editedLead.etiquetas, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setEditedLead({
      ...editedLead,
      etiquetas: editedLead.etiquetas.filter(t => t !== tag)
    });
  };

  const handleAddToHub = async (lead) => {
    try {
      const response = await axios.post(
        `${API}/hub/contacts/from-lead/${lead.id}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.data.success) {
        toast.success(t('leads.toast.addedToHub', 'Contacto agregado al Hub exitosamente'));
      } else {
        toast.error(response.data.error || t('leads.toast.alreadyInHub', 'Este contacto ya está en tu Hub'));
      }
    } catch (error) {
      console.error('Error adding to hub:', error);
      toast.error(t('leads.toast.addToHubError', 'Error al agregar al Hub'));
    }
  };

  const allTags = [...new Set(leads.flatMap(lead => lead.etiquetas || []))];
  const lockedCount = leads.filter(l => l.locked).length;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const leadsHoy = leads.filter(l => new Date(l.fecha_identificado) >= startOfToday).length;
  const accionados = leads.filter(l => l.estado !== 'Nuevo').length;
  const tasaRespuesta = leads.length > 0 ? Math.round((accionados / leads.length) * 100) : 0;
  const pendientesUrgentes = leads.filter(l => l.estado === 'Nuevo' && (Date.now() - new Date(l.fecha_identificado).getTime()) > 24 * 3600000).length;

  return (
    <div className="lp-wrap space-y-5">
      <style>{CSS}</style>

      {/* Header */}
      <div className="lp-header-row">
        <div>
          <h1 className="lp-header-title">{t('leads.title', 'Leads')}</h1>
          <p className="lp-header-sub">{t('leads.subtitle', 'Gestiona los contactos identificados de tu perfil')}</p>
        </div>
      </div>

      {/* First-visit onboarding (popup) */}
      {showOnboarding && (
        <div className="lp-onboarding-overlay" onClick={dismissOnboarding}>
          <div className="lp-onboarding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lp-onboarding-icon">
              <Icon name="target" className="w-5 h-5" />
            </div>
            <p className="lp-onboarding-title">{t('leads.onboarding.title', 'Bienvenido a Leads')}</p>
            <p className="lp-onboarding-desc">
              {t('leads.onboarding.desc', 'Cada vez que alguien complete el formulario de tu perfil o guarde tu tarjeta, aparece aquí automáticamente. Revisa sus datos, cambia su estado y haz seguimiento sin perder ningún contacto.')}
            </p>
            <button className="lp-onboarding-close" onClick={dismissOnboarding}>{t('leads.onboarding.close', 'Entendido')}</button>
          </div>
        </div>
      )}

      {/* Free plan quota banner */}
      {!loading && lockedCount > 0 && (
        <div className="lp-locked-banner">
          <span className="lp-locked-banner-text">
            <strong>{lockedCount}</strong> {lockedCount === 1 ? t('leads.lockedBanner.singular', 'lead bloqueado') : t('leads.lockedBanner.plural', 'leads bloqueados')} {t('leads.lockedBanner.suffix', 'este mes — superaste el cupo gratuito de 5.')}
          </span>
          <button className="lp-locked-banner-btn" onClick={() => openUpgrade('leads')}>
            {t('leads.lockedBanner.upgrade', 'Actualizar a Pro')}
          </button>
        </div>
      )}

      {/* Metrics */}
      {!loading && leads.length > 0 && (
        <div className="lp-metrics">
          <div className="lp-metric-card">
            <div className="lp-metric-value">{leadsHoy}</div>
            <div className="lp-metric-label">{t('leads.metrics.today', 'Leads hoy')}</div>
          </div>
          <div className="lp-metric-card">
            <div className="lp-metric-value">{tasaRespuesta}%</div>
            <div className="lp-metric-label">{t('leads.metrics.responseRate', 'Tasa de respuesta')}</div>
          </div>
          <div className="lp-metric-card">
            <div className="lp-metric-value" style={pendientesUrgentes > 0 ? { color: P.red } : undefined}>{pendientesUrgentes}</div>
            <div className="lp-metric-label">{t('leads.metrics.overdue', 'Pendientes hace +24h')}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="lp-filters">
        <input
          type="text"
          placeholder={t('leads.filters.searchPlaceholder', 'Buscar por nombre, teléfono o email...')}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="lp-input lp-input-search"
        />
        <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} className="lp-select">
          <option value="Todos">{t('leads.filters.allStatuses', 'Todos los estados')}</option>
          <option value="Nuevo">{estadoLabel('Nuevo', t)}</option>
          <option value="Contactado">{estadoLabel('Contactado', t)}</option>
          <option value="No contactado">{estadoLabel('No contactado', t)}</option>
        </select>
        {allTags.length > 0 && (
          <select value={etiquetaFilter} onChange={(e) => setEtiquetaFilter(e.target.value)} className="lp-select">
            <option value="">{t('leads.filters.allTags', 'Todas las etiquetas')}</option>
            {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
        )}
        {campaigns.length > 0 && (
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} className="lp-select">
            <option value="">{t('leads.filters.allCampaigns', 'Todas las campañas')}</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <input
          type="date"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          className="lp-input"
        />
        <input
          type="date"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          className="lp-input"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="lp-table-wrap" style={{ padding: 16 }}>
          <style>{SKELETON_CSS}</style>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[1, 2, 3, 4].map((row) => (
              <div key={row} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Skeleton width={140} height={14} />
                <Skeleton width={160} height={12} />
                <Skeleton width={70} height={20} radius={20} />
                <Skeleton width={80} height={12} />
              </div>
            ))}
          </div>
        </div>
      ) : leads.length === 0 ? (
        <div className="lp-empty">
          <div className="lp-empty-icon-mobile" style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <Icon name="users" className="w-6 h-6" style={{ color: '#aeaeb2' }} />
          </div>
          <p className="lp-empty-text" style={{ fontWeight: 500, color: P.dark, marginBottom: 4 }}>{t('leads.empty.title', 'Todavía no tienes leads')}</p>
          <p className="lp-empty-text lp-empty-text-sub" style={{ fontSize: 13, color: '#aeaeb2', marginBottom: 20 }}>{t('leads.empty.desc', 'Aparecerán aquí cuando alguien complete el formulario de contacto en tu perfil')}</p>
          {profile?.slug && (
            <button
              className="lp-btn-primary"
              style={{ display: 'block', maxWidth: 240, margin: '0 auto', padding: '0 20px' }}
              onClick={async () => {
                const url = `${window.location.origin}/pages/${encodeURIComponent(profile.slug)}`;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success(t('leads.toast.linkCopied', 'Enlace de tu perfil copiado'));
                } catch {
                  toast.error(t('leads.toast.linkCopyError', 'No se pudo copiar el enlace'));
                }
              }}
            >
              {t('leads.empty.copyLink', 'Copiar enlace de mi perfil')}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="lp-table-wrap">
            <table className="lp-table">
              <thead>
                <tr>
                  <th>{t('leads.table.contact', 'Contacto')}</th>
                  <th>{t('leads.table.info', 'Información')}</th>
                  <th>{t('leads.table.status', 'Estado')}</th>
                  <th>{t('leads.table.urgency', 'Urgencia')}</th>
                  <th>{t('leads.table.tags', 'Etiquetas')}</th>
                  <th>{t('leads.table.identified', 'Identificado')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  if (lead.locked) {
                    return (
                      <tr key={lead.id} className="lp-locked-row" onClick={() => openUpgrade('leads')}>
                        <td><span className="lp-locked-bar" style={{ width: 110 }} /></td>
                        <td><span className="lp-locked-bar" style={{ width: 140 }} /></td>
                        <td><span className={estadoBadgeClass(lead.estado)}>{estadoLabel(lead.estado, t)}</span></td>
                        <td><span className="lp-locked-bar" style={{ width: 60 }} /></td>
                        <td><span className="lp-locked-bar" style={{ width: 80 }} /></td>
                        <td className="lp-table-date">{new Date(lead.fecha_identificado).toLocaleDateString(dateLocale)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="lp-table-actions">
                            <button className="lp-locked-badge" onClick={() => openUpgrade('leads')}>
                              <Lock size={10} strokeWidth={2.5} /> Pro
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const urgency = urgencyBadge(lead, t);
                  return (
                    <tr key={lead.id} className="lp-table-row" onClick={() => handleViewLead(lead)}>
                      <td>
                        <span className="lp-card-name-group">
                          {lead.foto_url && <img src={lead.foto_url} alt={lead.nombre} className="lp-row-avatar" />}
                          <span className="lp-table-name">{lead.nombre}</span>
                          <button
                            className="lp-edit-icon-btn"
                            onClick={(e) => { e.stopPropagation(); handleViewLead(lead); }}
                            title={t('leads.table.editTitle', 'Editar lead')}
                          >
                            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </span>
                      </td>
                      <td>
                        {lead.telefono && (
                          <div className="lp-table-info-row"><Icon name="phone" className="w-3.5 h-3.5" />{lead.telefono}</div>
                        )}
                        {lead.email && (
                          <div className="lp-table-info-row"><Icon name="mail" className="w-3.5 h-3.5" />{lead.email}</div>
                        )}
                        {!lead.telefono && !lead.email && <span style={{ color: '#B0B0B5' }}>—</span>}
                      </td>
                      <td>
                        <span className={estadoBadgeClass(lead.estado)}>{estadoLabel(lead.estado, t)}</span>
                        {lead.estado === 'Nuevo' && (
                          <div className="lp-table-quick" onClick={(e) => e.stopPropagation()}>
                            <button className="lp-quick-btn lp-quick-btn-contact" onClick={() => handleQuickStatusChange(lead, 'Contactado')}>
                              {estadoLabel('Contactado', t)}
                            </button>
                            <button className="lp-quick-btn lp-quick-btn-skip" onClick={() => handleQuickStatusChange(lead, 'No contactado')}>
                              {estadoLabel('No contactado', t)}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="lp-urgency-badge" style={{ background: urgency.bg, color: urgency.color }}>
                          {urgency.label}
                        </span>
                      </td>
                      <td>
                        {lead.etiquetas && lead.etiquetas.length > 0 ? (
                          <div className="lp-tags">
                            {lead.etiquetas.map((tag, idx) => {
                              const tc = tagColor(tag);
                              return (
                                <span key={idx} className="lp-tag" style={{ background: tc.bg, color: tc.color }}>
                                  {tag}
                                </span>
                              );
                            })}
                          </div>
                        ) : <span style={{ color: '#B0B0B5' }}>—</span>}
                      </td>
                      <td className="lp-table-date">
                        {new Date(lead.fecha_identificado).toLocaleDateString(dateLocale)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="lp-table-actions">
                          {getContactHref(lead) ? (
                            <a href={getContactHref(lead)} className="lp-table-btn" title={t('leads.table.contactTitle', 'Contactar')}>
                              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {t('leads.table.contact', 'Contactar')}
                            </a>
                          ) : (
                            <span className="lp-table-btn lp-table-btn-disabled" title={t('leads.table.noContactInfo', 'Sin email ni teléfono registrado')}>
                              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {t('leads.table.contact', 'Contactar')}
                            </span>
                          )}
                          <button onClick={() => handleAddToHub(lead)} className="lp-table-btn" title={t('leads.table.addToHubTitle', 'Agregar a Hub')}>
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Hub
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked list */}
          <div className="lp-mobile-list">
            {leads.map((lead) => {
              if (lead.locked) {
                return (
                  <div key={lead.id} className="lp-mobile-card" style={{ background: '#FAFAFA' }} onClick={() => openUpgrade('leads')}>
                    <div className="lp-mobile-card-top">
                      <span className="lp-locked-bar" style={{ width: 120 }} />
                      <button className="lp-locked-badge" onClick={(e) => { e.stopPropagation(); openUpgrade('leads'); }}>
                        <Lock size={10} strokeWidth={2.5} /> Pro
                      </button>
                    </div>
                    <div className="lp-mobile-badges">
                      <span className={estadoBadgeClass(lead.estado)}>{estadoLabel(lead.estado, t)}</span>
                    </div>
                    <div className="lp-mobile-info">
                      <span className="lp-locked-bar" style={{ width: '70%' }} />
                    </div>
                  </div>
                );
              }
              const urgency = urgencyBadge(lead, t);
              return (
                <div key={lead.id} className="lp-mobile-card" onClick={() => handleViewLead(lead)}>
                  <div className="lp-mobile-card-top">
                    <span className="lp-card-name-group">
                      {lead.foto_url && <img src={lead.foto_url} alt={lead.nombre} className="lp-row-avatar" />}
                      <div className="lp-mobile-name">{lead.nombre}</div>
                    </span>
                    <span className="lp-urgency-badge" style={{ background: urgency.bg, color: urgency.color }}>
                      {urgency.label}
                    </span>
                  </div>
                  <div className="lp-mobile-badges">
                    <span className={estadoBadgeClass(lead.estado)}>{estadoLabel(lead.estado, t)}</span>
                    {lead.etiquetas && lead.etiquetas.map((tag, idx) => {
                      const tc = tagColor(tag);
                      return (
                        <span key={idx} className="lp-tag" style={{ background: tc.bg, color: tc.color }}>
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                  {(lead.telefono || lead.email) && (
                    <div className="lp-mobile-info">
                      {lead.telefono && (
                        <div className="lp-table-info-row"><Icon name="phone" className="w-3.5 h-3.5" />{lead.telefono}</div>
                      )}
                      {lead.email && (
                        <div className="lp-table-info-row"><Icon name="mail" className="w-3.5 h-3.5" />{lead.email}</div>
                      )}
                    </div>
                  )}
                  {lead.estado === 'Nuevo' && (
                    <div className="lp-table-quick" onClick={(e) => e.stopPropagation()}>
                      <button className="lp-quick-btn lp-quick-btn-contact" onClick={() => handleQuickStatusChange(lead, 'Contactado')}>
                        {estadoLabel('Contactado', t)}
                      </button>
                      <button className="lp-quick-btn lp-quick-btn-skip" onClick={() => handleQuickStatusChange(lead, 'No contactado')}>
                        {estadoLabel('No contactado', t)}
                      </button>
                    </div>
                  )}
                  <div className="lp-mobile-actions" onClick={(e) => e.stopPropagation()}>
                    {getContactHref(lead) ? (
                      <a href={getContactHref(lead)} className="lp-mobile-action-btn">
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('leads.table.contact', 'Contactar')}
                      </a>
                    ) : (
                      <span className="lp-mobile-action-btn lp-mobile-action-btn-disabled" title={t('leads.table.noContactInfo', 'Sin email ni teléfono registrado')}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('leads.table.contact', 'Contactar')}
                      </span>
                    )}
                    <button onClick={() => handleAddToHub(lead)} className="lp-mobile-action-btn">
                      <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Hub
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Lead Detail Modal */}
      {showLeadModal && editedLead && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <div className="lp-modal-inner">
              <div className="lp-modal-header">
                <h2 className="lp-modal-title">{t('leads.modal.title', 'Detalle del Lead')}</h2>
                <button onClick={() => setShowLeadModal(false)} className="lp-modal-close">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="lp-avatar">
                {editedLead.foto_url ? (
                  <img src={editedLead.foto_url} alt={editedLead.nombre} className="lp-avatar-img" />
                ) : (
                  <span className="lp-avatar-letter">{editedLead.nombre.charAt(0).toUpperCase()}</span>
                )}
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.name', 'Nombre')}</label>
                <input
                  type="text"
                  value={editedLead.nombre}
                  onChange={(e) => setEditedLead({ ...editedLead, nombre: e.target.value })}
                  className="lp-field-input"
                />
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.status', 'Estado')}</label>
                <select
                  value={editedLead.estado}
                  onChange={(e) => setEditedLead({ ...editedLead, estado: e.target.value })}
                  className="lp-field-select"
                >
                  <option value="Nuevo">{estadoLabel('Nuevo', t)}</option>
                  <option value="Contactado">{estadoLabel('Contactado', t)}</option>
                  <option value="No contactado">{estadoLabel('No contactado', t)}</option>
                </select>
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.phone', 'Teléfono')}</label>
                <input
                  type="tel"
                  value={editedLead.telefono || ''}
                  onChange={(e) => setEditedLead({ ...editedLead, telefono: e.target.value })}
                  className="lp-field-input"
                />
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.email', 'Email')}</label>
                <input
                  type="email"
                  value={editedLead.email || ''}
                  onChange={(e) => setEditedLead({ ...editedLead, email: e.target.value })}
                  className="lp-field-input"
                />
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.description', 'Descripción')}</label>
                <textarea
                  value={editedLead.descripcion || ''}
                  onChange={(e) => setEditedLead({ ...editedLead, descripcion: e.target.value })}
                  rows={3}
                  className="lp-field-textarea"
                />
              </div>

              <div className="lp-field">
                <label className="lp-label">{t('leads.modal.tags', 'Etiquetas')}</label>
                <div className="lp-tag-row">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    placeholder={t('leads.modal.newTagPlaceholder', 'Nueva etiqueta...')}
                    className="lp-tag-input"
                  />
                  <button onClick={handleAddTag} className="lp-tag-add">{t('leads.modal.addTag', 'Agregar')}</button>
                </div>
                <div className="lp-tags">
                  {editedLead.etiquetas && editedLead.etiquetas.map((tag, idx) => {
                    const tc = tagColor(tag);
                    return (
                      <span key={idx} className="lp-modal-tag" style={{ background: tc.bg, color: tc.color }}>
                        {tag}
                        <button onClick={() => handleRemoveTag(tag)} className="lp-modal-tag-remove">×</button>
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="lp-modal-footer">
                <button onClick={() => setShowLeadModal(false)} className="lp-btn-cancel">{t('leads.modal.cancel', 'Cancelar')}</button>
                <button onClick={handleSaveLead} className="lp-btn-save">{t('leads.modal.save', 'Guardar Cambios')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPanel;
